import { onScopeDispose, watch } from 'vue'

/**
 * Locks scrolling on the document while an overlay is open.
 *
 * All page shells scroll the document (AppLayout and DashboardLayout both use
 * min-h-screen), so an open modal would otherwise let the page scroll behind it.
 * A shared counter keeps stacked modals from unlocking each other.
 *
 * It used to add a padding to stand in for the scrollbar it had just removed.
 * That was right when it was written and became wrong: `scrollbar-gutter:stable`
 * is set on the document now, so the gutter is reserved whether or not a
 * scrollbar is in it, and hiding the overflow no longer takes any width away.
 * The padding was therefore adding fifteen pixels that nothing had removed, and
 * every page visibly jumped sideways as a dialog opened — the fault it exists to
 * prevent, caused by the fix for it.
 */
let lockCount = 0
let previousOverflow = ''

function lockDocument() {
  if (lockCount === 0) {
    previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
  }

  lockCount++
}

function unlockDocument() {
  if (lockCount === 0) {
    return
  }

  lockCount--

  if (lockCount === 0) {
    document.body.style.overflow = previousOverflow
  }
}

export function useScrollLock(isOpen) {
  let locked = false

  const sync = (open) => {
    if (open && !locked) {
      lockDocument()
      locked = true
      return
    }

    if (!open && locked) {
      unlockDocument()
      locked = false
    }
  }

  watch(isOpen, sync, { immediate: true })
  onScopeDispose(() => sync(false))
}
